// Password policy validation utilities
// Implements organization-wide password security rules
// Created: 2024-10-17
// CHANGE: Simplified validation with basic requirements only

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
  errorMessage: string;
}

// Password requirements
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'minLength',
    label: 'Au moins 10 caractères',
    test: (pwd) => pwd.length >= 10,
    errorMessage: 'Le mot de passe doit contenir au moins 10 caractères'
  },
  {
    id: 'uppercase',
    label: 'Au moins une lettre majuscule (A-Z)',
    test: (pwd) => /[A-Z]/.test(pwd),
    errorMessage: 'Le mot de passe doit contenir au moins une lettre majuscule'
  },
  {
    id: 'lowercase',
    label: 'Au moins une lettre minuscule (a-z)',
    test: (pwd) => /[a-z]/.test(pwd),
    errorMessage: 'Le mot de passe doit contenir au moins une lettre minuscule'
  },
  {
    id: 'number',
    label: 'Au moins un chiffre (0-9)',
    test: (pwd) => /[0-9]/.test(pwd),
    errorMessage: 'Le mot de passe doit contenir au moins un chiffre'
  },
  {
    id: 'special',
    label: 'Au moins un caractère spécial (!@#$%...)',
    test: (pwd) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd),
    errorMessage: 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)'
  }
];

// Main validation function
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  // Check all requirements
  PASSWORD_REQUIREMENTS.forEach(req => {
    if (!req.test(password)) {
      errors.push(req.errorMessage);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

// Check if password meets minimum requirements (used for quick validation)
export function meetsMinimumRequirements(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every(req => req.test(password));
}

// Get list of unmet requirements
export function getUnmetRequirements(password: string): PasswordRequirement[] {
  return PASSWORD_REQUIREMENTS.filter(req => !req.test(password));
}

// Format error messages for display
export function formatValidationErrors(result: PasswordValidationResult): string {
  if (result.isValid) return '';
  return result.errors.join('\n');
}
